import type { Axios, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios, { isAxiosError } from 'axios';

import { API_VERSIONS, CONFIG } from '@/constants/endpoints';
import { runtimeEnv } from '@/config/runtime-env';
import type { APIService } from '@/types';
import { showError } from '@/modules/notification/engine/notification.library';
import { useAuthenticationStore } from '@/store';

declare module 'axios' {
	export interface AxiosRequestConfig {
		skipGenericErrorHandler?: boolean;
		skipSuccessCheck?: boolean;
	}
}

export class AxiosService implements APIService {
	axiosInstance: AxiosInstance;

	constructor(config?: AxiosRequestConfig & { apiVersion: string }) {
		const defaultConfig: AxiosRequestConfig = {
			baseURL: `${runtimeEnv.apiBaseUrl}${config?.apiVersion ?? API_VERSIONS.DEFAULT}`,
			timeout: CONFIG.timeout,
		};
		const resultinConfig = { ...defaultConfig, ...config };
		this.axiosInstance = axios.create(resultinConfig);

		this.axiosInstance.interceptors.response.use(
			response => {
				if (response.config?.skipSuccessCheck || response.data?.Success !== false) return response;

				// Create a fake Axios error and reject
				const error: any = new Error(response?.data.ErrorMessage ?? '');
				error.response = response;
				error.isAxiosError = true;
				// Preserve the original config to maintain skipGenericErrorHandler functionality
				error.config = response.config;
				return Promise.reject(error);
			},
			error => {
				// Optionally, handle error responses as before
				return Promise.reject(error);
			}
		);
		// Add a response interceptor
		this.axiosInstance.interceptors.response.use(
			response => response,
			error => {
				if (error.config?.skipGenericErrorHandler) {
					return Promise.reject(error);
				}

				/* Handle 401 Unauthorized - redirect to login
				At first glance such logic may look too agressive. But staying with 401 error is also bad. After all, in classic server side rendering we would do that also on first resource auth error.
				*/
				if (isAxiosError(error) && error.response?.status === 401) {
					// Clear authentication state
					const { resetState } = useAuthenticationStore.getState();
					resetState();

					// Redirect to login page
					window.location.href = '/login';

					// Don't show error toast for 401 as redirect handles it
					return Promise.reject(error);
				}

				var msg = '';
				if (isAxiosError(error)) {
					msg = error.message ?? 'An error occurred';
					// Check for validation errors (status 400)
					const status = error.response?.status;
					const errors = error.response?.data?.errors;
					if (status === 400 && errors && typeof errors === 'object') {
						// Flatten all error messages into a single string
						const validationMessages = Object.values(errors)
							.flat()
							.join('\n');
						msg += `\n${validationMessages}`;
					} else if ((status === 400 || status == 500) && error.response?.data?.ErrorMessage) {
						msg += `\n${error.response?.data?.ErrorMessage}`;
					}
				}
				else {
					msg = 'An unexpected error occurred';
				}
				showError(msg, { summary: 'Error' });
				console.error(msg);

				// Optionally, you can reject the error if you want to handle it further downstream
				return Promise.reject(error);
			}
		);

		this.axiosInstance.interceptors.request.use(
			(config) => {

				const { accessToken } = useAuthenticationStore.getState();
				if (accessToken) {
					config.headers = config.headers || {};
					config.headers['Authorization'] = `Bearer ${accessToken}`;
				}
				return config;
			},
			(error) => Promise.reject(error)
		);

	}

	getClient(): AxiosInstance {
		return this.axiosInstance;
	}

	setToken(token: string): void {
		this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
	}

	removeToken(): void {
		this.axiosInstance.defaults.headers.common.Authorization = undefined;
	}
}
// it should be app singleton
export const axiosService = new AxiosService();
export const axiosInstance = axiosService.axiosInstance;

export const axiosSwaggerService = new AxiosService({
	baseURL: `${runtimeEnv.apiBaseUrl}`,
	timeout: CONFIG.timeout,
	apiVersion: 'v1',
	// workaround for https://github.com/RicoSuter/NSwag/issues/4973. NSwag parses json itself, so need to disable the parsing of axios.
	transformResponse: [(data) => data]
});
export const axiosSwaggerInstance = axiosSwaggerService.axiosInstance;



//export default axiosService;
